import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { AgentEditScope, ChangeRequestStatus } from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, Description, Heading } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { CHANGE_REQUEST_NEXT_STEPS } from '@services/content-agents/browser';

import {
    apiQuery,
    apiQueryClient,
    useAssertCurrentOrganizationId,
    useCurrentOrganizationId,
} from '../../state';
import {
    agentHasMessages,
    agentIsProcessing,
    agentIsSynced,
    generateConversationLocalId,
    useAgentChatAnalytics,
    useOnAgentTurnEnd,
    useAgentInputPrompt,
    useAgentState,
    useChangeRequestAgent,
    selectAgentEditTarget,
    type OrganizationAgentLocation,
    useOrganizationAgent,
    useOrganizationBackgroundAgent,
    useReportAgentLocation,
    useAgentEditTargetChangeRequest,
    useRecordChangeRequestMerged,
    useAgentPendingPrompt,
} from '../Agent';
import {
    AgentChat,
    type AgentChatSubmitInput,
    AgentEditScopePicker,
    getAgentEditScope,
    useAgentChatInputValue,
    usePostAgentMessage,
    useAgentMessageLimit,
} from '../AgentChat';
import { useSiteSetupChatSteps, useSiteSetupGuideStarter } from '../AgentSiteSetup';
import { ContentReferenceContextProvider } from '../ContentReferences';
import { ContentIcon } from '../Icons';
import { AgentDockConversationMenu } from './AgentDockConversationMenu';
import { AgentDockWelcome } from './AgentDockWelcome';
import { type AgentConversationHistory, AgentHistoryMenu } from './AgentHistoryMenu';
import { hasChangeRequestContentChanges } from './changeRequestNextSteps';
import {
    AnalyticsContextBanner,
    LocationContextBanner,
    LocationContextChips,
    LocationContextAway,
    LocationContextSwitch,
    LocationContextToggle,
    useLocationContext,
} from './LocationContext';
import {
    type AgentDockTab,
    useAgentDockAnalytics,
    useAgentDockPage,
    useIsAgentDockDraftConversation,
    useOpenAgentDockTab,
} from './state';
import {
    type AgentDockWelcomeContext,
    compileAgentDockStarter,
    getAgentDockWelcome,
    SITE_SETUP_GUIDE_STARTER_ID,
} from './welcome';

/**
 * Why the editing scopes are not on offer to someone who may not edit the space. The agent
 * forces read-only for them, so the picker shows the reason rather than a choice it will
 * silently override.
 */
const EDIT_SCOPES_NEEDING_EDIT_ACCESS: Partial<Record<AgentEditScope, string>> = {
    [AgentEditScope.All]: 'You need edit access to this space to change content',
    [AgentEditScope.CurrentPage]: 'You need edit access to this space to change content',
    [AgentEditScope.ModifiedPages]: 'You need edit access to this space to change content',
};

/** Offered once the agent has changes in an open change request. More changes are typed in the composer. */
const CHANGE_REQUEST_NEXT_STEP_LABELS = CHANGE_REQUEST_NEXT_STEPS.map((step) => step.label);

/** Offered on a site when the agent has no suggestions of its own, so the user always has a way on. */
const SITE_NEXT_STEPS = [
    'Suggest how to improve the site structure',
    'Import content from a URL',
    'Write a new page',
];

/**
 * Prompt shown in the composer. Once there is a conversation to continue, "reply" describes what
 * the box does better than an invitation to start one.
 */
function getPlaceholder(hasMessages: boolean): string {
    return hasMessages ? 'Reply or ask for more changes...' : 'Ask GitBook...';
}

/**
 * One conversation, with the chrome its surface asks for.
 *
 * The same thing whether it is read in the dock's window or in the panel anchored to the side: only
 * the controls in its header differ, since the two surfaces can do different things with it.
 */
export function AgentDockConversation(
    props: {
        tab: AgentDockTab;
        /** Where the user is, so the agent can resolve "this site" without going looking. */
        location: OrganizationAgentLocation;
        /** Whether this conversation has not received its first message yet. */
        isDraft?: boolean;
        /** Extra layout applied to the chat by its surface. */
        chatClassName?: string;
    } & AgentDockConversationHeaderContent &
        AgentDockConversationControls
) {
    const { tab, location, isDraft, chatClassName, ...header } = props;
    const organizationId = useAssertCurrentOrganizationId();

    const conversation = (() => {
        if (tab.kind === 'organization') {
            return (
                <OrganizationConversation
                    conversationId={tab.conversationId}
                    location={location}
                    isDraft={isDraft}
                    chatClassName={chatClassName}
                    {...header}
                />
            );
        }

        if (tab.kind === 'organization_background') {
            return <BackgroundAgentConversation tab={tab} {...header} />;
        }

        return <ChangeRequestConversation tab={tab} {...header} />;
    })();

    return (
        <ContentReferenceContextProvider value={{ organization: organizationId }}>
            {conversation}
        </ContentReferenceContextProvider>
    );
}

/**
 * A background agent's own transcript, opened from the conversation that started it: how it reached
 * the report its parent acted on. The agent refuses messages, so there is no composer.
 */
function BackgroundAgentConversation(
    props: {
        tab: Extract<AgentDockTab, { kind: 'organization_background' }>;
    } & AgentDockConversationHeaderContent &
        AgentDockConversationControls
) {
    const { tab, onStarted: _onStarted, ...header } = props;
    const organizationId = useCurrentOrganizationId();
    const openTab = useOpenAgentDockTab(organizationId ?? '');

    const agent = useOrganizationBackgroundAgent({
        organizationId: organizationId ?? '',
        conversationId: tab.conversationId,
    });
    // One opened after it reported is not still working, whatever the dock last heard.
    const isWorking = useAgentState(agent, agentIsProcessing);

    const openParent = useEventCallback(() => {
        openTab({ kind: 'organization', conversationId: tab.parentConversationId });
    });

    if (!agent) {
        return null;
    }

    return (
        <>
            <AgentDockConversationHeader {...header} />
            {/* The banner floats over the transcript rather than sitting under it: this
                conversation has no composer, so the foot of the window is empty space the
                transcript scrolls behind. */}
            <div className="relative flex min-h-0 flex-1">
                <AgentChat
                    className="min-h-0"
                    agent={agent}
                    withInput={false}
                    floating
                    // Clearance at the end of the transcript, so the banner floating over it does
                    // not sit on the last thing the agent said.
                    messageFlowFooter={<div aria-hidden className="h-14 shrink-0" />}
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center p-3">
                    <div className="pointer-events-auto flex max-w-full items-center gap-2 rounded-lg border border-neutral-subtle bg-base py-1.5 pr-1.5 pl-3 shadow-overlay">
                        <ContentIcon icon="robot" className="size-3.5 shrink-0 text-muted" />
                        <Description className="min-w-0 truncate text-ui-small">
                            {isWorking ? 'Working in the background' : 'Finished in the background'}
                        </Description>
                        <Button
                            kind="secondary"
                            size="small"
                            label="Back to chat"
                            onPress={openParent}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * What a conversation's header shows, which every surface hands over with the conversation itself.
 */
type AgentDockConversationHeaderContent = {
    title: string;
    /** Optional icon displayed beside the plain title. */
    titleIcon?: React.ReactNode;
    /** Extra layout applied to the header by its surface. */
    headerClassName?: string;
    /**
     * The other conversations, which the title opens. Passed only by the surface with nowhere else
     * to put them: the dock keeps a history button in its tab row, and its title stays a plain
     * heading.
     */
    history?: AgentConversationHistory;
    /** Controls the surface adds of its own, before the conversation's. */
    actions?: React.ReactNode;
    /** Replaces the title, for a surface that names the conversation its own way. */
    heading?: React.ReactNode;
};

/**
 * What a surface lets the user do with the conversation in it.
 *
 * Every control is optional because the two surfaces offer different ones: the window can be
 * closed, minimized and anchored, the panel can only be sent back to the dock, and a draft has
 * nothing to delete.
 */
export type AgentDockConversationControls = {
    onClose?: () => void;
    onMinimize?: () => void;
    /** Move the agent into the layout's rail. Offered by the window, on a screen wide enough. */
    onAnchor?: () => void;
    /** Move the agent back to the bar at the bottom. Offered by the panel. */
    onUnanchor?: () => void;
    /**
     * Called once the conversation holds its first message, which is when a draft stops being a
     * draft and earns a tab.
     */
    onStarted?: () => void;
    onDelete?: () => Promise<void> | void;
    /** Open another conversation in the surface's place, as its history does. */
    onOpenConversation?: (tab: AgentDockTab) => void;
};

/**
 * The conversation's title and its controls.
 *
 * Whether the title is also a control depends on the surface. The anchored panel has no row of tabs
 * to hold a history button, so its title carries the history itself — one place saying which
 * conversation is being read and offering the rest of them. The dock has that row, and keeps its
 * button there.
 */
function AgentDockConversationHeader(
    props: AgentDockConversationHeaderContent & Omit<AgentDockConversationControls, 'onStarted'>
) {
    const {
        title,
        titleIcon,
        headerClassName,
        history,
        actions,
        heading,
        onMinimize,
        onClose,
        onAnchor,
        onUnanchor,
        onDelete,
    } = props;

    return (
        <div
            className={clsx(
                'flex shrink-0 justify-between p-3 pr-2.5',
                headerClassName ?? 'pb-0',
                heading ? 'gap-4 pl-3' : history ? 'gap-1 pl-2.5' : 'gap-1 pl-4.5'
            )}
        >
            {heading ? (
                heading
            ) : history ? (
                <Heading level={2} className="min-w-0">
                    <AgentHistoryMenu
                        history={history}
                        trigger={
                            <Button
                                kind="blank"
                                size="small"
                                label={title}
                                labelStyle="text-ui-action! font-semibold text-base"
                                trailingIcon={Icon.ChevronDown}
                                // Tighter than a button's own gap, so the chevron reads as part of
                                // the title rather than as something sitting next to it.
                                className="gap-1"
                            />
                        }
                    />
                </Heading>
            ) : (
                <Heading
                    level={2}
                    className="flex min-w-0 flex-1 items-center gap-2 text-ui-action font-semibold select-none"
                >
                    {titleIcon}
                    <span className="truncate">{title}</span>
                </Heading>
            )}

            <div className="flex shrink-0 items-center">
                {actions}
                <AgentDockConversationMenu {...(onDelete ? { onDelete } : {})} />
                {onAnchor ? (
                    <Button
                        kind="blank"
                        size="small"
                        leadingIcon={Icon.AIAgentSidesheet}
                        label="Anchor as side panel"
                        iconOnly
                        onPress={onAnchor}
                    />
                ) : null}
                {onUnanchor ? (
                    <Button
                        kind="blank"
                        size="small"
                        leadingIcon={Icon.ArrowDownLeft}
                        label="Move to dock"
                        iconOnly
                        onPress={onUnanchor}
                    />
                ) : null}
                {onMinimize ? (
                    <Button
                        kind="blank"
                        size="small"
                        leadingIcon={Icon.Minus}
                        label="Minimize"
                        iconOnly
                        onPress={onMinimize}
                    />
                ) : null}
                {onClose ? (
                    <Button
                        kind="blank"
                        size="small"
                        leadingIcon={Icon.X}
                        label="Close conversation"
                        iconOnly
                        onPress={onClose}
                    />
                ) : null}
            </div>
        </div>
    );
}

function OrganizationConversation(
    props: {
        conversationId: string;
        location: OrganizationAgentLocation;
        isDraft?: boolean;
        chatClassName?: string;
    } & AgentDockConversationHeaderContent &
        AgentDockConversationControls
) {
    const {
        conversationId,
        location,
        isDraft,
        chatClassName,
        onStarted,
        onOpenConversation,
        ...header
    } = props;
    const organizationId = useCurrentOrganizationId();
    const agent = useOrganizationAgent({
        // The dock only renders inside an organization, so this is always set here.
        organizationId: organizationId ?? '',
        conversationId,
    });
    // The site next steps are only offered on the site agent screen for now.
    const isSiteSetupEnabled = location.surface === 'agent_screen';
    useReportAgentLocation({ agent, location });
    const hasMessages = useAgentState(agent, agentHasMessages);
    const synced = useAgentState(agent, agentIsSynced);
    const editScope = useAgentState(agent, getAgentEditScope);
    const editTarget = useAgentState(agent, selectAgentEditTarget);
    const changeRequestQuery = useAgentEditTargetChangeRequest(editTarget);
    useOnAgentTurnEnd(agent, () => {
        if (editTarget?.changeRequestId) {
            void apiQueryClient.invalidateQueries({
                queryKey: apiQuery('getChangeRequestById', {
                    spaceId: editTarget.spaceId,
                    changeRequestId: editTarget.changeRequestId,
                }).queryKey,
            });
        }
    });
    const changeRequestInProgress =
        changeRequestQuery.data?.status === ChangeRequestStatus.Draft ||
        changeRequestQuery.data?.status === ChangeRequestStatus.Open;
    const changeRequestHasContentChanges = changeRequestQuery.data
        ? hasChangeRequestContentChanges(changeRequestQuery.data)
        : false;
    const hasChangeRequestNextSteps = changeRequestInProgress && changeRequestHasContentChanges;
    useRecordChangeRequestMerged({
        agent,
        changeRequestId: editTarget?.changeRequestId ?? undefined,
        merged: changeRequestQuery.data?.status === ChangeRequestStatus.Merged,
    });
    const hasAgentSuggestions = useAgentState(agent, agentHasSuggestions);
    // The page the user is on, whether or not it is in the message context: the context only holds
    // a page of the target the conversation works in, while the picker has to say whether there is
    // a page to focus on at all.
    const dockPage = useAgentDockPage();
    const openTab = useOpenAgentDockTab(organizationId ?? '');
    // Its own tab, so the conversation that started it stays where it was: reading what a
    // background agent did is a detour, not a move.
    const openBackgroundAgent = useEventCallback((backgroundAgent: api.AgentBackgroundAgent) => {
        openTab({
            kind: 'organization_background',
            conversationId: backgroundAgent.conversationId,
            parentConversationId: conversationId,
            title: backgroundAgent.title,
        });
    });
    // A conversation nobody has written in holds nothing, whatever the agent has had time to say
    // about it — so the welcome goes up on the first paint instead of after the connection.
    const dockDraft = useIsAgentDockDraftConversation(organizationId ?? '', conversationId);
    const startsEmpty = isDraft ?? dockDraft;
    // Anything else is only known to be empty once the agent has sent what it holds. Until then
    // the client has no messages because it has not been told any, and reading that as an empty
    // conversation offers a started one the context it settled long ago.
    const started = !startsEmpty && !(synced && !hasMessages);
    const inputKey = `agentDock:${conversationId}`;
    const input = useAgentChatInputValue(inputKey);
    const locationContext = useLocationContext({ agent, location, started });
    const dockAnalytics = useAgentDockAnalytics();
    const analytics = dockAnalytics?.siteId === location.siteId ? dockAnalytics : null;

    // What an empty conversation can offer to start with: where the user is, less whatever they
    // took out of the context — a change request they removed is not one the conversation is about.
    const { offered: offeredContext, included: includedContext } = locationContext;
    const keepTarget = offeredContext === null || includedContext.target;
    const siteSetupGuideStarter = useSiteSetupGuideStarter({
        organizationId: organizationId ?? '',
        location,
        onOpenConversation,
    });
    const welcomeContext: AgentDockWelcomeContext = {
        // Unknown while it loads, so the regular site welcome does not flash first.
        siteSetup: siteSetupGuideStarter.guide,
        location: {
            ...(location.siteId ? { siteId: location.siteId } : {}),
            ...(keepTarget && location.spaceId ? { spaceId: location.spaceId } : {}),
            ...(keepTarget && location.changeRequest !== undefined
                ? { changeRequest: location.changeRequest }
                : {}),
        },
        page: Boolean(offeredContext?.page) && includedContext.target && includedContext.page,
        selection:
            Boolean(offeredContext?.selection) &&
            includedContext.target &&
            includedContext.selection,
        analytics: analytics !== null,
    };

    const { onTrackReachLimit, onTrackOpenUpgrade, onTrackPickStarter, ...chatTracking } =
        useAgentChatAnalytics({
            kind: 'organization',
            organizationId: organizationId ?? '',
            // Unknown for the moment a site's publication state is still loading.
            place: getAgentDockWelcome(welcomeContext)?.place,
        });
    const limit = useAgentMessageLimit({
        hasMessages: started,
        onTrackReachLimit,
        onTrackOpenUpgrade,
    });

    // Promote the draft the moment it holds a message. Watched here rather than hooked onto the
    // submit handler so a message sent from anywhere — a restored outbox, another surface — counts.
    const onStartedEvent = useEventCallback(() => onStarted?.());
    React.useEffect(() => {
        if (hasMessages) {
            onStartedEvent();
        }
    }, [hasMessages, onStartedEvent]);

    // The context still goes out ahead of the message — the agent builds the turn's scope from
    // what is attached when the message arrives, so the delivery waits on the attach — but the
    // user does not: the message enters the outbox, and the conversation view, immediately.
    const submitMessage = useEventCallback(({ message }: AgentChatSubmitInput) => {
        agent?.postMessage(message, { deliverAfter: locationContext.send() });
    });

    // Sent the way the chat sends a suggestion: checked against the message limit, counted, tracked.
    const { postSuggestion } = usePostAgentMessage({
        submitMessage,
        currentContext: locationContext.messageContext,
        isSubmissionBlocked: limit.isSubmissionBlocked,
        onSubmissionBlocked: limit.onSubmissionBlocked,
        onTrackPostMessage: chatTracking.onTrackPostMessage,
        getPreviousMessagesCount: () => agent?.state.messages.length ?? 0,
    });
    // A site's setup conversation, on any surface, while in its site.
    const siteSetup = useSiteSetupChatSteps({
        agent,
        conversationId,
        location,
        postSuggestion,
        onOpenNewConversation: () =>
            onOpenConversation?.({
                kind: 'organization',
                conversationId: generateConversationLocalId(),
            }),
    });

    // A prompt handed over by whoever opened this conversation — the editor's selection menu, an
    // empty block, the page header. Written into the composer rather than sent, so the user still
    // reads it and can adjust it before the agent sees it — unless it is to be sent, as the setup
    // guide's start is.
    const { pendingPrompt, submitPendingPrompt, consumePendingPrompt } =
        useAgentPendingPrompt(conversationId);
    const setInputWithPrompt = useAgentInputPrompt({ inputKey });
    React.useEffect(() => {
        if (!pendingPrompt) {
            return;
        }
        if (submitPendingPrompt) {
            postSuggestion(pendingPrompt);
        } else {
            setInputWithPrompt(pendingPrompt);
        }
        consumePendingPrompt();
    }, [
        pendingPrompt,
        submitPendingPrompt,
        setInputWithPrompt,
        consumePendingPrompt,
        postSuggestion,
    ]);

    if (!agent) {
        return null;
    }

    const chat = (
        <AgentChat
            className={clsx('min-h-0', chatClassName)}
            agent={agent}
            input={input}
            inputKey={inputKey}
            floating
            placeholder={
                hasChangeRequestNextSteps
                    ? 'Describe more changes, or pick a next step...'
                    : getPlaceholder(started)
            }
            submitMessage={submitMessage}
            onOpenBackgroundAgent={openBackgroundAgent}
            currentContext={locationContext.messageContext}
            {...chatTracking}
            isSubmissionBlocked={limit.isSubmissionBlocked}
            onSubmissionBlocked={limit.onSubmissionBlocked}
            messageFlowHeader={siteSetup?.header}
            messageFlowFooter={limit.messageFlowFooter ?? siteSetup?.footer}
            startsEmpty={startsEmpty}
            renderEmptyState={({ startWith }) => (
                <AgentDockWelcome
                    context={welcomeContext}
                    onPickStarter={({ starter, place }) => {
                        onTrackPickStarter({
                            starter: starter.id,
                            place,
                            immediate: starter.immediate,
                        });
                        if (starter.id === SITE_SETUP_GUIDE_STARTER_ID) {
                            siteSetupGuideStarter.start(starter.prompt);
                            return;
                        }
                        startWith(compileAgentDockStarter(starter));
                    }}
                />
            )}
            bannerAboveInput={[
                limit.bannerAboveInput,
                /* Rendered even while excluded: the banner animates its own hiding, sliding
                   behind the composer, which an unmount would cut short. The key is what
                   lets the chat's presence boundary play the same slide when the offer ends
                   and the banner does unmount — which is also why these are an array and
                   not a fragment, since a fragment would hide both keys from it. */
                locationContext.offered ? (
                    <LocationContextBanner
                        key="location-context"
                        context={locationContext.offered}
                        included={locationContext.included}
                        onToggle={locationContext.toggle}
                    />
                ) : null,
                // What the conversation is about to start with; the agent reads the report
                // from the URL with every message after that.
                analytics && !started ? (
                    <AnalyticsContextBanner key="analytics-context" analytics={analytics} />
                ) : null,
            ]}
            // Once the first message has attached the target, its tray takes over from the
            // banner above — and the page and the selection move into it, so they stay
            // where they have been all along and go out with every message from there.
            // When the user has walked to another change request instead, the same spot
            // says so and offers to bring the agent along.
            editTargetContext={
                locationContext.attached ? (
                    <LocationContextChips
                        context={locationContext.attached}
                        included={locationContext.included}
                        onToggle={locationContext.toggle}
                    />
                ) : locationContext.userTargetMismatch?.kind === 'editable' ? (
                    <LocationContextSwitch
                        agentTarget={locationContext.userTargetMismatch.agentTarget}
                        target={locationContext.userTargetMismatch.target}
                        siteId={location.siteId}
                        onSwitch={locationContext.switchTarget}
                        blocked={locationContext.switchBlocked}
                    />
                ) : locationContext.userTargetMismatch?.kind === 'readonly' ? (
                    <LocationContextAway target={locationContext.userTargetMismatch.agentTarget} />
                ) : null
            }
            {...(hasChangeRequestNextSteps
                ? {
                      nextSteps: [
                          ...CHANGE_REQUEST_NEXT_STEP_LABELS,
                          ...(siteSetup?.changeRequestNextSteps ?? []),
                      ],
                  }
                : siteSetup?.step
                  ? { question: siteSetup.question }
                  : isSiteSetupEnabled && started && location.siteId && !hasAgentSuggestions
                    ? { nextSteps: SITE_NEXT_STEPS }
                    : {})}
            inputToolbarActions={
                <>
                    <AgentEditScopePicker
                        editScope={editScope}
                        // This agent works across the whole organization, so the narrower
                        // scopes need something to point at. Both are anchored on a page
                        // the user is editing, which the space screen only publishes for a
                        // change request or a space with live edits unlocked — elsewhere
                        // there is no page in the sense these scopes mean, so they say what
                        // would make them available rather than sitting there inert.
                        unavailableScopes={{
                            ...(dockPage
                                ? {}
                                : {
                                      [AgentEditScope.CurrentPage]:
                                          'Only available inside a change request',
                                  }),
                            ...(location.changeRequest !== undefined
                                ? {}
                                : {
                                      [AgentEditScope.ModifiedPages]:
                                          'Only available inside a change request',
                                  }),
                        }}
                        onSelect={(scope) => Promise.resolve(agent.actions.setEditScope(scope))}
                    />
                    {locationContext.offered ? (
                        <LocationContextToggle
                            context={locationContext.offered}
                            included={locationContext.included.target}
                            onToggle={() => locationContext.toggle('target')}
                        />
                    ) : null}
                </>
            }
        />
    );

    return (
        <>
            <AgentDockConversationHeader {...header} />
            <div className="flex min-h-0 flex-1">
                {siteSetup ? siteSetup.chatSiteSetupStepsProvider(chat) : chat}
            </div>
        </>
    );
}

/**
 * A conversation held by the older change-request agent, opened from the history.
 *
 * Produced by background work, so the dock offers it to be read where it happened rather than as a
 * place to start something new.
 */
function ChangeRequestConversation(
    props: {
        tab: Extract<AgentDockTab, { kind: 'change_request' }>;
    } & AgentDockConversationHeaderContent &
        AgentDockConversationControls
) {
    const { tab, onStarted: _onStarted, ...header } = props;
    const organizationId = useCurrentOrganizationId();

    // Read from the cache the change request screen already filled, so this costs no request of its
    // own. It answers two questions: whether the agent may be used at all, and the space its events
    // are attributed to.
    const { data: space, isError: spaceFailed } = useQuery(
        apiQuery('getSpaceById', { spaceId: tab.spaceId })
    );
    // Undefined only while the answer is on its way. A lookup that fails means the space is gone
    // or out of reach, which is the only thing that makes the agent unusable — what the user may do
    // once connected is the agent's own business, and it tells them.
    const spaceReachable = space ? true : spaceFailed ? false : undefined;
    // Separate from reachability: a member who may not edit still gets the agent, and this is only
    // what the edit-scope picker offers them.
    const canEditSpace = space ? space.permissions.edit : undefined;

    const agent = useChangeRequestAgent({
        organizationId: organizationId ?? '',
        spaceId: tab.spaceId,
        changeRequestId: tab.changeRequestId,
        conversationId: tab.conversationId,
        enabled: spaceReachable !== false,
    });
    const hasMessages = useAgentState(agent, agentHasMessages);
    const inputKey = `agentDock:cr:${tab.changeRequestId}:${tab.conversationId ?? 'default'}`;
    const input = useAgentChatInputValue(inputKey);

    // Until the space lands the events carry the organization alone.
    const {
        onTrackReachLimit,
        onTrackOpenUpgrade,
        // Never fired here: these conversations come with messages, so they never show the welcome.
        onTrackPickStarter: _onTrackPickStarter,
        ...chatTracking
    } = useAgentChatAnalytics(
        space
            ? { kind: 'change_request', space, changeRequestId: tab.changeRequestId }
            : { kind: 'organization', organizationId: organizationId ?? '' }
    );
    const limit = useAgentMessageLimit({ hasMessages, onTrackReachLimit, onTrackOpenUpgrade });
    const editScope = useAgentState(agent, getAgentEditScope);

    // Where the user is, so a reply like "fix this heading" resolves to the page they are reading.
    // The side panel this window replaced always sent it; without it the agent has a change request
    // and no idea which of its pages is meant.
    const dockPage = useAgentDockPage();
    const currentContext = React.useMemo((): api.AIMessageContext | undefined => {
        // The change request as well as the space: a tab restored from a previous session, or opened
        // in another window, can be on a different change request of the same space than the one the
        // user is reading — and a page id resolves against a branch, so the space matching is not
        // enough to make it this tab's page. Compared by id, which is what a tab carries; the page
        // publishes its number too, but only the dock's own location is addressed that way.
        if (
            !dockPage ||
            dockPage.spaceId !== tab.spaceId ||
            dockPage.changeRequest?.id !== tab.changeRequestId
        ) {
            return undefined;
        }
        return { location: { spaceId: dockPage.spaceId, pageId: dockPage.page.id } };
    }, [dockPage, tab.spaceId, tab.changeRequestId]);

    // Only when the space itself is out of reach. A member who may not edit still gets the agent:
    // it reads and plans with them, and says what it cannot do when they ask for a change.
    if (spaceReachable === false) {
        return (
            <>
                <AgentDockConversationHeader {...header} />
                <div className="flex min-h-0 flex-1 items-center justify-center p-6">
                    <Description className="text-center">
                        This conversation belongs to content you can no longer reach.
                    </Description>
                </div>
            </>
        );
    }

    if (!agent) {
        return null;
    }

    return (
        <>
            <AgentDockConversationHeader {...header} />
            <div className="flex min-h-0 flex-1">
                <AgentChat
                    className="min-h-0"
                    agent={agent}
                    input={input}
                    inputKey={inputKey}
                    floating
                    placeholder={getPlaceholder(hasMessages)}
                    currentContext={currentContext}
                    {...chatTracking}
                    isSubmissionBlocked={limit.isSubmissionBlocked}
                    onSubmissionBlocked={limit.onSubmissionBlocked}
                    bannerAboveInput={limit.bannerAboveInput}
                    messageFlowFooter={limit.messageFlowFooter}
                    inputToolbarActions={
                        <AgentEditScopePicker
                            editScope={editScope}
                            // A member who may not edit this space is read-only whatever they pick,
                            // so the picker says so rather than offering a choice the server
                            // overrides in silence.
                            fallbackScope={
                                canEditSpace === false
                                    ? AgentEditScope.ReadOnly
                                    : AgentEditScope.All
                            }
                            // Always inside a change request here, so "changed pages" always
                            // applies; "current page" still needs a page open. Read from the dock's
                            // published page rather than the message context, which stops being
                            // offered after the first message.
                            unavailableScopes={
                                canEditSpace === false
                                    ? EDIT_SCOPES_NEEDING_EDIT_ACCESS
                                    : dockPage
                                      ? {}
                                      : {
                                            [AgentEditScope.CurrentPage]:
                                                'Open a page to focus on it',
                                        }
                            }
                            onSelect={(scope) => Promise.resolve(agent.actions.setEditScope(scope))}
                        />
                    }
                />
            </div>
        </>
    );
}

function agentHasSuggestions(state: { suggestions: string[] }): boolean {
    return state.suggestions.length > 0;
}
